import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

/**
 * #481 图片马赛克 - 拼图过渡效果
 * 
 * 分类：转场/过渡
 * 来源：中优先级列表
 * 技术：CSS Grid + Spring 动画
 * 时长：5 秒（150 帧）
 * 特点：
 *   - 6x4 网格拼图动画（24 块）
 *   - Spring 物理翻转效果
 *   - 渐变色彩过渡
 *   - 3D 透视效果
 * 用途：图片切换、视频转场、产品展示
 */

interface ImageMosaicProps {
  gridColumns?: number;
  gridRows?: number;
  flipDelay?: number;
  color1?: string;
  color2?: string;
}

export const ImageMosaic: React.FC<ImageMosaicProps> = ({
  gridColumns = 6,
  gridRows = 4,
  flipDelay = 0.1,
  color1 = '#FF6B6B',
  color2 = '#4ECDC4',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  const totalTiles = gridColumns * gridRows;
  const tileWidth = width / gridColumns;
  const tileHeight = height / gridRows;
  
  return (
    <AbsoluteFill
      style={{
        perspective: '1200px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
        }}
      >
        {Array.from({ length: totalTiles }).map((_, index) => {
          const col = index % gridColumns;
          const row = Math.floor(index / gridColumns);
          
          // 螺旋式延迟
          const centerX = gridColumns / 2;
          const centerY = gridRows / 2;
          const distance = Math.sqrt(Math.pow(col - centerX, 2) + Math.pow(row - centerY, 2));
          const maxDistance = Math.sqrt(Math.pow(centerX, 2) + Math.pow(centerY, 2));
          
          const delay = (distance / maxDistance) * 60 * flipDelay; // 帧数延迟
          
          const rotation = spring({
            frame: frame - delay,
            fps,
            config: {
              damping: 15,
              stiffness: 100,
              mass: 0.8,
            },
          });
          
          const rotateY = interpolate(rotation, [0, 1], [0, 180]);
          
          // 根据位置计算颜色
          const progress = (col + row) / (gridColumns + gridRows - 2);
          const hue = interpolate(progress, [0, 1], [0, 360]);
          const tileColor = `hsl(${hue}, 70%, 60%)`;
          
          const opacity = interpolate(
            rotation,
            [0, 0.5, 1],
            [1, 0.3, 1]
          );
          
          return (
            <div
              key={index}
              style={{
                width: tileWidth,
                height: tileHeight,
                position: 'relative',
                transformStyle: 'preserve-3d',
                transform: `rotateY(${rotateY}deg)`,
                transition: 'transform 0.05s linear',
              }}
            >
              {/* 正面 */}
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  background: color1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: tileWidth * 0.3,
                  fontWeight: 'bold',
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  opacity,
                }}
              >
                {index + 1}
              </div>
              
              {/* 背面 */}
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  background: `linear-gradient(135deg, ${tileColor} 0%, ${color2} 100%)`,
                  transform: 'rotateY(180deg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)',
                }}
              >
                <div
                  style={{
                    width: tileWidth * 0.6,
                    height: tileHeight * 0.6,
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: tileWidth * 0.2,
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  {String.fromCharCode(65 + (index % 26))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* 装饰文字 */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 32,
          color: 'rgba(255,255,255,0.8)',
          fontWeight: 'bold',
          letterSpacing: '2px',
          opacity: interpolate(frame, [100, 120], [0, 1], { extrapolateLeft: 'clamp' }),
        }}
      >
        Image Mosaic Transition
      </div>
      
      {/* 统计信息 */}
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'rgba(0,0,0,0.5)',
          padding: '12px 20px',
          borderRadius: 8,
          color: 'white',
          fontSize: 24,
          opacity: interpolate(frame, [80, 100], [0, 1], { extrapolateLeft: 'clamp' }),
        }}
      >
        <div>网格: {gridColumns} × {gridRows}</div>
        <div>总块数: {totalTiles}</div>
      </div>
    </AbsoluteFill>
  );
};
